import { httpProxy } from '../api'

export type ReportTier = 'free' | 'pro' | 'agency'

export type ReportLanguage = 'en' | 'es' | 'ca'

export interface ReportListItem {
    id: number
    generatedAt: string
    tier: ReportTier
    language: ReportLanguage
    periodStart: string
    periodEnd: string
    sizeBytes: number | null
    sha256: string | null
}

export interface PaginatedResponse<T> {
    success: boolean
    currentTier: ReportTier
    canGenerateOnDemand: boolean
    reports: T[]
    pagination: {
        page: number
        limit: number
        total: number
        totalPages: number
    }
}

export interface ReportDetail extends ReportListItem {
    downloadUrl: string
    downloadUrlExpiresInSeconds: number
}

export interface ManualGenerateResponse {
    success: boolean
    report?: ReportListItem | null
    error?: string
    reason?: string
}

export type EnsureFirstReportStatus =
    | 'ready'
    | 'waiting_for_analysis'
    | 'generating'

export interface EnsureFirstReportResponse {
    success: boolean
    status: EnsureFirstReportStatus
    report?: ReportListItem | null
}

export async function getWeeklyReports(
    brandId: number,
    page: number = 1,
    limit: number = 10
): Promise<PaginatedResponse<ReportListItem>> {
    const res = await httpProxy.get(`brand/${brandId}/reports`, {
        params: { page, limit },
    })
    return res.data
}

export async function getWeeklyReportById(
    brandId: number,
    reportId: number
): Promise<ReportDetail> {
    const res = await httpProxy.get(`brand/${brandId}/reports/${reportId}`)
    return res.data
}

/**
 * Pro-only manual generation. Resolves with the new report metadata when the
 * backend finishes generating; rejects (axios error) for 403 (not pro),
 * 409 (already in flight), 503 (storage not configured), or 5xx (generation failure).
 */
export async function generateWeeklyReport(
    brandId: number,
    language?: ReportLanguage
): Promise<ManualGenerateResponse> {
    const res = await httpProxy.post(`brand/${brandId}/reports/generate`, {
        ...(language ? { language } : {}),
    })
    return res.data
}

/**
 * Idempotent: starts first-report generation after onboarding analysis when needed.
 */
export async function ensureFirstWeeklyReport(brandId: number): Promise<EnsureFirstReportResponse> {
    const res = await httpProxy.post(`brand/${brandId}/reports/ensure-first`)
    return res.data
}

// ---------------------------------------------------------------------------
// White-label report branding (paid Agency plan only)
// ---------------------------------------------------------------------------

export type ReportLogoMode = 'default' | 'custom' | 'website'

/** Client-side guards mirroring the backend validation. */
export const REPORT_LOGO_MAX_BYTES = 2 * 1024 * 1024
export const REPORT_LOGO_ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp']

export interface ReportBrandingConfig {
    success: boolean
    mode: ReportLogoMode
    hasCustomLogo: boolean
    customLogoUrl: string | null
    landingUrl?: string | null
    updatedAt?: string | null
}

export async function getReportBranding(brandId: number): Promise<ReportBrandingConfig> {
    const res = await httpProxy.get(`brand/${brandId}/report-logo`)
    return res.data
}

/**
 * Switch the active logo source. New custom logos are uploaded via
 * {@link uploadReportLogo}; `mode: 'custom'` only re-selects an already-stored
 * custom logo.
 */
export async function setReportBrandingMode(
    brandId: number,
    mode: ReportLogoMode
): Promise<ReportBrandingConfig> {
    const res = await httpProxy.put(`brand/${brandId}/report-logo`, { mode })
    return res.data
}

export async function uploadReportLogo(
    brandId: number,
    file: File
): Promise<ReportBrandingConfig> {
    const form = new FormData()
    form.append('logo', file)

    const res = await httpProxy.post(`brand/${brandId}/report-logo/upload`, form, {
        // Let the browser set multipart/form-data with the correct boundary.
        headers: { 'Content-Type': undefined },
    })
    return res.data
}
